import React, { useEffect, useState, useRef } from 'react'
import Header from '../../components/Header'
import { AUTH_URL } from '../../common/common'
import CustomButton from '../../components/AddButton'
import { useNavigate } from 'react-router-dom'
import { Column, EmployeeData } from '../../service/interfaces/Table.interface'
import AdminContainer from '../../containers/Admin'
import CustomTable from '../../components/Table'
import { ApiService } from '../../service/api.service'
import { employee, Plus } from '../../common/svg'
import ToggleSwitch from '../../components/ToggleSwitch/ToggleSwitch'
import ToggleActive from '../../components/ToggleSwitch/ToggleActive'

const Employee: React.FC = () => {
    const navigate = useNavigate();
    const apiService = new ApiService();
    const entityId = localStorage.getItem("entity_id");

    const [employeeData, setEmployeeData] = useState<EmployeeData[]>([]);
    const [search, setSearch] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [active, setActive] = useState<boolean>(true);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleEdit = (item: EmployeeData) => {
        navigate(`edit/${item.employeeid}`);
    };

    // Handle toggle employee active status
    const handleToggleActive = async (employeeId: string, isActive: boolean) => {
        try {
            const response: any = await apiService.putApi(
                (`${AUTH_URL}/employee/status-route`),
                {
                    employeeId: employeeId,
                    isActive: isActive ? 1 : 0
                }
            );
            
            if (response.status === "200") {
                // Update local state to reflect the change
                setEmployeeData(prevData => 
                    prevData.map(employee => 
                        employee.employeeid === employeeId ? { ...employee, isActive } : employee
                    )
                );
        
            }
        } catch (error) {
            console.error("Error updating employee status:", error);
          
        }
    };

    const getEmployeeData = async (isLoadingMore = false) => {
        if (!hasMoreData && isLoadingMore) return;
 
        setLoading(true);
        try {
            const response: any = await apiService.getApi(
                `${AUTH_URL}/employee/get-route?page=${currentPage}&limit=${pageSize}&search=${search}&entityId=${entityId}&status=${active?1:0}`
            );

            if (response.status === "200") {
                const formattedData = response.body?.data?.map((employee: any) => ({
                    firstname: employee.firstname || "N/A",
                    lastname: employee.lastname || "N/A",
                    emailid: employee.emailid || "N/A",
                    mobilenumber: employee.mobilenumber || "N/A",
                    employeeid: employee.employeeid,
                    isActive: employee?.isActive === 1 // Convert 1/0 to true/false
                })) || [];

                const totalRecords = parseInt(response.body.pagination?.totalRecords) || 0;
                const currentLoadedItems = isLoadingMore 
                    ? employeeData.length + formattedData.length 
                    : formattedData.length;

                setHasMoreData(currentLoadedItems < totalRecords);

                setEmployeeData(prevData => 
                    isLoadingMore ? [...prevData, ...formattedData] : formattedData
                );

                setTotalItems(totalRecords);
            }
        } catch (error) {
            console.error('Error fetching employee data:', error);
            setHasMoreData(false);
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        if (hasMoreData) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            getEmployeeData(true);
        }
    };

    useEffect(() => {
        setHasMoreData(true);
        setCurrentPage(1);
        getEmployeeData();
    }, [search, entityId, active]);

    const handleAdd = () => {
        navigate('add');
    };

    const handleRowClick = (item: EmployeeData) => {
        // navigate(`view/${item.employeeid}`);
    };

    const columns: Column<EmployeeData>[] = [
        { header: "First Name", dataKey: "firstname" },
        { header: "Last Name", dataKey: "lastname" },
        { header: "Email Id", dataKey: "emailid" },
        { 
            header: "Mobile Number", 
            dataKey: "mobilenumber",
            render: (value: string | boolean | undefined, row: EmployeeData) => (
                <div className="text-sm text-left leading-relaxed">
                    {typeof value === 'string' ? value : 'N/A'}
                </div>
            )
        },
        {
            header: "Status",
            dataKey: "isActive",
            render: (value: string | boolean | undefined) => {
                let status: string | null = null;
            
                if (typeof value === "boolean") {
                    status = value ? "Active" : "Inactive";
                } else if (typeof value === "string") {
                    if (value === "1") {
                        status = "Active";
                    } else if (value === "0") {
                        status = "Inactive";
                    } else {
                        return (
                            <div>
                                {value
                                    .split("\n")
                                    .map((line, i) => <div key={i}>{line}</div>)}
                            </div>
                        );
                    }
                }
            
                return <div>{status}</div>;
            }
        },
        { 
            header: "Actions",
            dataKey: "employeeid", 
            render: (value, row) => (  
                <div className="relative" ref={activeMenu === value ? menuRef : null}>
                    <button 
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(activeMenu === value ? null : typeof value === 'string' ? value : null);
                        }}
                    >
                        <div className="flex flex-col items-center justify-center h-5">
                            <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
                            <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
                            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                        </div>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeMenu === value && (
                        <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
                            <button
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(row);
                                    setActiveMenu(null);
                                }}
                            >
                                Edit
                            </button>
                            
                            {/* Toggle Switch instead of Delete button */}
                            <div className="px-4 py-2">
                                <ToggleSwitch
                                    showLabel={true}
                                    checked={row.isActive}
                                    onChange={(isActive) => {
                                        handleToggleActive(row.employeeid as string, isActive);
                                        setActiveMenu(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )
        }
    ];

    return (
        <div>
            <Header 
                icon={employee} 
                title="Employees" 
                showSearch={true} 
                setSearch={setSearch}
                
                toggleComponent={
                    <div className="flex items-center gap-2 ml-[420px]">
                    <ToggleActive
                        onChange={(isActive: boolean) => {
                            setActive(isActive);
                            console.log(isActive ? "active 1" : "inactive 0");
                        }}
                    />
                    </div>
                }
            >
                <CustomButton
                    label=""
                    onClick={handleAdd}
                    icon={Plus}
                />
            </Header>
            <AdminContainer>
                <CustomTable<EmployeeData>
                    columns={columns}
                    data={employeeData}
                    onRowClick={handleRowClick}
                    loading={loading}
                    onLoadMore={handleLoadMore}
                />
            </AdminContainer>
        </div>
    )
}

export default Employee